// S41/S43: Admin Account Requests Management Page (Closures & Reopens)
import { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Space,
  Card,
  Button,
  Typography,
  message,
  Modal,
  Input,
  InputNumber,
  Select,
  Alert,
  Dropdown,
  Tabs,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  adminClosureRequestsEndpoints,
  type ClosureRequest,
} from '../api/adminClosureRequestsEndpoints';
import {
  adminReopenRequestsEndpoints,
  type ReopenRequest,
} from '../api/adminReopenRequestsEndpoints';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

export function AdminRequests() {
  const [activeTab, setActiveTab] = useState<'closures' | 'reopens'>('closures');
  
  // Closure requests state
  const [closureLoading, setClosureLoading] = useState(false);
  const [closureRequests, setClosureRequests] = useState<ClosureRequest[]>([]);
  const [closurePagination, setClosurePagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [closureStatusFilter, setClosureStatusFilter] = useState<string>('pending');
  const [closureRejectModalOpen, setClosureRejectModalOpen] = useState(false);
  const [selectedClosureRequest, setSelectedClosureRequest] = useState<ClosureRequest | null>(null);
  const [closureRejectReason, setClosureRejectReason] = useState('');

  // Reopen requests state
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenRequests, setReopenRequests] = useState<ReopenRequest[]>([]);
  const [reopenPagination, setReopenPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [reopenStatusFilter, setReopenStatusFilter] = useState<string>('pending_admin_review');
  const [reopenApproveModalOpen, setReopenApproveModalOpen] = useState(false);
  const [reopenRejectModalOpen, setReopenRejectModalOpen] = useState(false);
  const [selectedReopenRequest, setSelectedReopenRequest] = useState<ReopenRequest | null>(null);
  const [requiredContribution, setRequiredContribution] = useState<number | null>(null);
  const [reopenRejectReason, setReopenRejectReason] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'closures') {
      fetchClosureRequests();
    } else {
      fetchReopenRequests();
    }
  }, [activeTab, closurePagination.current, closurePagination.pageSize, closureStatusFilter, reopenPagination.current, reopenPagination.pageSize, reopenStatusFilter]);

  // Closure Requests Functions
  const fetchClosureRequests = async () => {
    setClosureLoading(true);
    try {
      const data = await adminClosureRequestsEndpoints.getClosureRequests({
        status: closureStatusFilter as any,
        page: closurePagination.current,
        per_page: closurePagination.pageSize,
      });
      
      setClosureRequests(data.closure_requests || []);
      setClosurePagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));
    } catch (error: any) {
      message.error('Failed to load closure requests');
    } finally {
      setClosureLoading(false);
    }
  };

  const handleClosureApprove = async (request: ClosureRequest) => {
    try {
      await adminClosureRequestsEndpoints.approveClosureRequest(request.id);
      message.success(`Closure request approved for ${request.user.full_name}`);
      fetchClosureRequests();
    } catch (error: any) {
      message.error(error.message || 'Failed to approve closure request');
    }
  };

  const showClosureRejectModal = (request: ClosureRequest) => {
    setSelectedClosureRequest(request);
    setClosureRejectReason('');
    setClosureRejectModalOpen(true);
  };

  const handleClosureReject = async () => {
    if (!selectedClosureRequest) return;

    try {
      await adminClosureRequestsEndpoints.rejectClosureRequest(
        selectedClosureRequest.id,
        closureRejectReason
      );
      message.success(`Closure request rejected for ${selectedClosureRequest.user.full_name}`);
      setClosureRejectModalOpen(false);
      setSelectedClosureRequest(null);
      setClosureRejectReason('');
      fetchClosureRequests();
    } catch (error: any) {
      message.error(error.message || 'Failed to reject closure request');
    }
  };

  // Reopen Requests Functions
  const fetchReopenRequests = async () => {
    setReopenLoading(true);
    try {
      const data = await adminReopenRequestsEndpoints.getReopenRequests({
        status: reopenStatusFilter as any,
        page: reopenPagination.current,
        per_page: reopenPagination.pageSize,
      });
      
      setReopenRequests(data.reopen_requests || []);
      setReopenPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));
    } catch (error: any) {
      message.error('Failed to load reopen requests');
    } finally {
      setReopenLoading(false);
    }
  };

  const showReopenApproveModal = (request: ReopenRequest) => {
    setSelectedReopenRequest(request);
    setRequiredContribution(request.user.min_required_contribution_cents ? 
      request.user.min_required_contribution_cents / 100 : 1000);
    setReopenApproveModalOpen(true);
  };

  const handleReopenApprove = async () => {
    if (!selectedReopenRequest || !requiredContribution || requiredContribution <= 0) {
      message.error('Please enter a valid required contribution amount');
      return;
    }

    try {
      await adminReopenRequestsEndpoints.approveReopenRequest(
        selectedReopenRequest.id,
        requiredContribution * 100
      );
      message.success(`Reopen request approved for ${selectedReopenRequest.user.full_name}`);
      setReopenApproveModalOpen(false);
      setSelectedReopenRequest(null);
      setRequiredContribution(null);
      fetchReopenRequests();
    } catch (error: any) {
      message.error(error.message || 'Failed to approve reopen request');
    }
  };

  const showReopenRejectModal = (request: ReopenRequest) => {
    setSelectedReopenRequest(request);
    setReopenRejectReason('');
    setReopenRejectModalOpen(true);
  };

  const handleReopenReject = async () => {
    if (!selectedReopenRequest) return;

    try {
      await adminReopenRequestsEndpoints.rejectReopenRequest(
        selectedReopenRequest.id,
        reopenRejectReason
      );
      message.success(`Reopen request rejected for ${selectedReopenRequest.user.full_name}`);
      setReopenRejectModalOpen(false);
      setSelectedReopenRequest(null);
      setReopenRejectReason('');
      fetchReopenRequests();
    } catch (error: any) {
      message.error(error.message || 'Failed to reject reopen request');
    }
  };

  const getClosureStatusTag = (status: string) => {
    switch (status) {
      case 'pending':
        return <Tag icon={<ClockCircleOutlined />} color="processing">Pending</Tag>;
      case 'processing':
        return <Tag icon={<ClockCircleOutlined />} color="warning">Processing</Tag>;
      case 'completed':
        return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
      case 'cancelled':
        return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getReopenStatusTag = (status: string) => {
    switch (status) {
      case 'pending_admin_review':
        return <Tag icon={<ClockCircleOutlined />} color="processing">Pending Review</Tag>;
      case 'approved':
        return <Tag icon={<CheckCircleOutlined />} color="success">Approved</Tag>;
      case 'rejected':
        return <Tag icon={<CloseCircleOutlined />} color="error">Rejected</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const closureColumns: ColumnsType<ClosureRequest> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.user.full_name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Request Date',
      dataIndex: 'requested_at',
      key: 'requested_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.requested_at).unix() - dayjs(b.requested_at).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getClosureStatusTag(status),
    },
    {
      title: 'Balances',
      key: 'balances',
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text>Base: ${(record.user.base_principal_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Withdrawable: ${(record.user.withdrawable_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isPending = record.status === 'pending';
        
        if (!isPending) {
          return (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/users/${record.user.id}`)}
            >
              View User
            </Button>
          );
        }

        const menuItems = [
          {
            key: 'approve',
            label: 'Approve',
            icon: <CheckCircleOutlined />,
            onClick: () => {
              Modal.confirm({
                title: 'Approve Closure Request',
                content: `Approve account closure for ${record.user.full_name}? This will process their final settlement and create a final withdrawal.`,
                okText: 'Approve',
                okButtonProps: { icon: <CheckCircleOutlined /> },
                onOk: () => handleClosureApprove(record),
              });
            },
          },
          {
            key: 'reject',
            label: 'Reject',
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () => showClosureRejectModal(record),
          },
          {
            key: 'view',
            label: 'View User',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/admin/users/${record.user.id}`),
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="primary">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const reopenColumns: ColumnsType<ReopenRequest> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.user.full_name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Previous Exit',
      key: 'exit',
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text>{dayjs(record.user.exit_effective_at).format('MMM D, YYYY')}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Retained until: {record.user.retained_until ? 
              dayjs(record.user.retained_until).format('MMM D, YYYY') : 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Request Date',
      dataIndex: 'initiated_at',
      key: 'initiated_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.initiated_at).unix() - dayjs(b.initiated_at).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getReopenStatusTag(status),
    },
    {
      title: 'Required Amount',
      dataIndex: 'required_contribution_cents',
      key: 'required_contribution_cents',
      render: (cents: number | null) => 
        cents ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isPending = record.status === 'pending_admin_review';
        
        if (!isPending) {
          return (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/users/${record.user.id}`)}
            >
              View User
            </Button>
          );
        }

        const menuItems = [
          {
            key: 'approve',
            label: 'Approve & Set Amount',
            icon: <CheckCircleOutlined />,
            onClick: () => showReopenApproveModal(record),
          },
          {
            key: 'reject',
            label: 'Reject',
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () => showReopenRejectModal(record),
          },
          {
            key: 'view',
            label: 'View User',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/admin/users/${record.user.id}`),
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="primary">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        title="Account Requests"
        description="Manage account closure and reopen requests"
      />

      <Card style={{ marginTop: '24px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'closures' | 'reopens')}
          items={[
            {
              key: 'closures',
              label: (
                <span>
                  <CloseCircleOutlined /> Closure Requests
                </span>
              ),
              children: (
                <>
                  <Space style={{ marginBottom: '16px', width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Text>Filter by status:</Text>
                      <Select
                        style={{ width: 200 }}
                        value={closureStatusFilter}
                        onChange={setClosureStatusFilter}
                        options={[
                          { label: 'Pending', value: 'pending' },
                          { label: 'Processing', value: 'processing' },
                          { label: 'Completed', value: 'completed' },
                          { label: 'Cancelled', value: 'cancelled' },
                          { label: 'All', value: '' },
                        ]}
                      />
                    </Space>
                    <Button onClick={fetchClosureRequests}>Refresh</Button>
                  </Space>

                  <Alert
                    message="Closure Process"
                    description="When you approve a closure request, the user will receive their final profit allocation in the next settlement, after which a final withdrawal will be created for their total balance."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />

                  <Table
                    columns={closureColumns}
                    dataSource={closureRequests}
                    loading={closureLoading}
                    rowKey="id"
                    pagination={{
                      ...closurePagination,
                      onChange: (page, pageSize) => {
                        setClosurePagination(prev => ({ ...prev, current: page, pageSize }));
                      },
                    }}
                  />
                </>
              ),
            },
            {
              key: 'reopens',
              label: (
                <span>
                  <ReloadOutlined /> Reopen Requests
                </span>
              ),
              children: (
                <>
                  <Space style={{ marginBottom: '16px', width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Text>Filter by status:</Text>
                      <Select
                        style={{ width: 200 }}
                        value={reopenStatusFilter}
                        onChange={setReopenStatusFilter}
                        options={[
                          { label: 'Pending Review', value: 'pending_admin_review' },
                          { label: 'Approved', value: 'approved' },
                          { label: 'Rejected', value: 'rejected' },
                          { label: 'All', value: '' },
                        ]}
                      />
                    </Space>
                    <Button onClick={fetchReopenRequests}>Refresh</Button>
                  </Space>

                  <Alert
                    message="Reopening Process"
                    description="When you approve a reopen request, the user will need to fund their account with the new required amount before they can participate in profit allocations again. Their previous participation history is preserved."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />

                  <Table
                    columns={reopenColumns}
                    dataSource={reopenRequests}
                    loading={reopenLoading}
                    rowKey="id"
                    pagination={{
                      ...reopenPagination,
                      onChange: (page, pageSize) => {
                        setReopenPagination(prev => ({ ...prev, current: page, pageSize }));
                      },
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* Closure Reject Modal */}
      <Modal
        title="Reject Closure Request"
        open={closureRejectModalOpen}
        onOk={handleClosureReject}
        onCancel={() => {
          setClosureRejectModalOpen(false);
          setSelectedClosureRequest(null);
          setClosureRejectReason('');
        }}
        okText="Reject"
        okButtonProps={{ danger: true, icon: <CloseCircleOutlined /> }}
      >
        {selectedClosureRequest && (
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="Rejecting Closure Request"
              description={`This will deny ${selectedClosureRequest.user.full_name}'s request to close their account. You can optionally provide a reason.`}
              type="error"
              showIcon
            />
            
            <div>
              <Text strong>Reason (optional):</Text>
              <TextArea
                rows={4}
                value={closureRejectReason}
                onChange={(e) => setClosureRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                style={{ marginTop: '8px' }}
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* Reopen Approve Modal */}
      <Modal
        title="Approve Reopen Request"
        open={reopenApproveModalOpen}
        onOk={handleReopenApprove}
        onCancel={() => {
          setReopenApproveModalOpen(false);
          setSelectedReopenRequest(null);
          setRequiredContribution(null);
        }}
        okText="Approve"
        okButtonProps={{ icon: <CheckCircleOutlined /> }}
      >
        {selectedReopenRequest && (
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="Set New Funding Requirement"
              description={`${selectedReopenRequest.user.full_name} will need to fund their account with this amount before becoming active again. Their previous base principal will not be restored.`}
              type="warning"
              showIcon
            />
            
            <div>
              <Text strong>User Information:</Text>
              <div style={{ marginTop: '8px' }}>
                <div><Text>Name: </Text><Text strong>{selectedReopenRequest.user.full_name}</Text></div>
                <div><Text>Email: </Text><Text>{selectedReopenRequest.user.email}</Text></div>
                <div><Text>Exited: </Text><Text>{dayjs(selectedReopenRequest.user.exit_effective_at).format('MMM D, YYYY')}</Text></div>
                <div><Text>Previous Base Principal: </Text>
                  <Text>${(selectedReopenRequest.user.base_principal_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Text>
                </div>
              </div>
            </div>

            <div>
              <Text strong>Required Contribution Amount (USD):</Text>
              <InputNumber
                style={{ width: '100%', marginTop: '8px' }}
                min={0}
                precision={2}
                value={requiredContribution}
                onChange={(value) => setRequiredContribution(value)}
                prefix="$"
                placeholder="Enter required amount"
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Suggested: Previous minimum was ${(selectedReopenRequest.user.min_required_contribution_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Text>
            </div>
          </Space>
        )}
      </Modal>

      {/* Reopen Reject Modal */}
      <Modal
        title="Reject Reopen Request"
        open={reopenRejectModalOpen}
        onOk={handleReopenReject}
        onCancel={() => {
          setReopenRejectModalOpen(false);
          setSelectedReopenRequest(null);
          setReopenRejectReason('');
        }}
        okText="Reject"
        okButtonProps={{ danger: true, icon: <CloseCircleOutlined /> }}
      >
        {selectedReopenRequest && (
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="Rejecting Reopen Request"
              description={`This will deny ${selectedReopenRequest.user.full_name}'s request to rejoin the pool. You can optionally provide a reason.`}
              type="error"
              showIcon
            />
            
            <div>
              <Text strong>Reason (optional):</Text>
              <TextArea
                rows={4}
                value={reopenRejectReason}
                onChange={(e) => setReopenRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                style={{ marginTop: '8px' }}
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}
